import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../utils/supabase';

interface SuperAdmin {
  id: string;
  userId: string;
  email: string;
  name: string | null;
}

interface SuperAdminAuthContextType {
  admin: SuperAdmin | null;
  token: string | null;
  loading: boolean;
  signOut: () => Promise<void>;
  updateAuth: (token: string, admin: SuperAdmin) => void;
}

const SuperAdminAuthContext = createContext<SuperAdminAuthContextType | undefined>(undefined);

export function useSuperAdminAuth() {
  const context = useContext(SuperAdminAuthContext);
  if (!context) {
    throw new Error('useSuperAdminAuth must be used within a SuperAdminAuthProvider');
  }
  return context;
}

export const SuperAdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<SuperAdmin | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for Supabase session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.access_token) {
        const storedAdmin = localStorage.getItem('superAdmin');
        if (storedAdmin) {
          try {
            setToken(session.access_token);
            setAdmin(JSON.parse(storedAdmin));
          } catch (e) {
            // Invalid stored data, clear it
            localStorage.removeItem('superAdmin');
          }
        }
      }
      setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token) {
        setToken(session.access_token);
        const storedAdmin = localStorage.getItem('superAdmin');
        if (storedAdmin) {
          try {
            setAdmin(JSON.parse(storedAdmin));
          } catch (e) {
            localStorage.removeItem('superAdmin');
          }
        }
      } else {
        setToken(null);
        setAdmin(null);
        localStorage.removeItem('superAdmin');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdmin');
    setAdmin(null);
    setToken(null);
  };

  const updateAuth = (newToken: string, newAdmin: SuperAdmin) => {
    setToken(newToken);
    setAdmin(newAdmin);
    localStorage.setItem('superAdminToken', newToken);
    localStorage.setItem('superAdmin', JSON.stringify(newAdmin));
  };

  return (
    <SuperAdminAuthContext.Provider value={{ admin, token, loading, signOut, updateAuth }}>
      {children}
    </SuperAdminAuthContext.Provider>
  );
};

